// This file is automatically generated. Do not edit it directly.
import { createClient } from '@supabase/supabase-js';
import { Database } from '@/types/supabase';

// Get environment variables with fallbacks and debug info
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || "https://jftsemrnjqbzinphyrpm.supabase.co";
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY || "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImpmdHNlbXJuanFiemlucGh5cnBtIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDIxMjI2NTksImV4cCI6MjA1NzY5ODY1OX0.6LHVHXbLg-fRFy9DlXvpLTs3J0w31RIGieVD6_zujys";
const SUPABASE_SERVICE_KEY = import.meta.env.VITE_SUPABASE_SERVICE_ROLE_KEY;

// TEMPORARY FLAG: Set to false to disable realtime features for testing
export const ENABLE_REALTIME = false;

// Debug logging
//console.log("Supabase Configuration:", {
//  URL: SUPABASE_URL,
//  KEY: SUPABASE_ANON_KEY ? "Present" : "Missing",
//  SERVICE_KEY: SUPABASE_SERVICE_KEY ? "Present" : "Missing"
//});

// Initialize variables for singleton instances
let supabaseInstance = createClient<Database>(
  SUPABASE_URL,
  SUPABASE_ANON_KEY,
  {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      flowType: 'pkce',
      storage: window.localStorage,
      storageKey: 'roundone.auth.token',
      debug: false
    },
    global: {
      headers: {
        'x-client-info': 'roundone-web'
      }
    },
    realtime: {
      params: {
        eventsPerSecond: 10
      }
    }
  }
);

// Set up session refresh monitoring with improved error handling
supabaseInstance.auth.onAuthStateChange(async (event, session) => {
  // Only log errors, not regular auth state changes
  if (event === 'SIGNED_OUT') {
    window.localStorage.removeItem('roundone.auth.token');
  } else if (event === 'PASSWORD_RECOVERY') {
    // Handle password recovery event
    const currentUrl = new URL(window.location.href);
    if (!currentUrl.pathname.includes('/auth/reset-password')) {
      window.location.href = `${window.location.origin}/auth/reset-password`;
    }
  }
});

// Set up connection state monitoring with improved reconnection logic
const systemChannel = supabaseInstance.channel('system')
  .on('presence', { event: 'sync' }, () => {
    // Removed console log
  })
  .on('presence', { event: 'join' }, () => {
    // Removed console log
  })
  .on('presence', { event: 'leave' }, () => {
    // Removed console log
  })
  .subscribe((status) => {
    if (status === 'CLOSED') {
      // Exponential backoff for reconnection attempts
      let retryCount = 0;
      const maxRetries = 5;
      const retryConnection = () => {
        if (retryCount < maxRetries) {
          setTimeout(() => {
            systemChannel.subscribe();
            retryCount++;
          }, Math.min(1000 * Math.pow(2, retryCount), 30000));
        } else {
          console.error('Failed to reconnect after maximum attempts');
        }
      };
      retryConnection();
    }
  });

// Function to check and reconnect realtime with improved error handling
const checkRealtimeConnection = async () => {
  try {
    if (!supabaseInstance.realtime.isConnected()) {
      await supabaseInstance.realtime.connect();
    }
    
    // Also check session validity
    const { data: { session }, error } = await supabaseInstance.auth.getSession();
    if (error) {
      console.error('Error checking session:', error);
      // Attempt to refresh the session
      const { data: { session: refreshedSession }, error: refreshError } = 
        await supabaseInstance.auth.refreshSession();
      if (refreshError) {
        console.error('Error refreshing session:', refreshError);
      }
    } else if (session) {
      // Check if token will expire soon (within 5 minutes)
      const expiresAt = session.expires_at;
      const expiresIn = expiresAt ? (expiresAt * 1000) - Date.now() : 0;
      
      // If token expires in less than 5 minutes, refresh it proactively
      if (expiresIn < 5 * 60 * 1000) {
        // console.log('Token expiring soon, refreshing proactively');
        const { data: { session: refreshedSession }, error: refreshError } = 
          await supabaseInstance.auth.refreshSession();
        if (refreshError) {
          console.error('Error refreshing session:', refreshError);
        }
      }
    }
  } catch (error) {
    console.error('Error in checkRealtimeConnection:', error);
  }
};

// Check connection more frequently to avoid token expiration issues
// setInterval(checkRealtimeConnection, 60000); // TEMP: Disable periodic check

// Check connection when tab becomes visible
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') {
    // console.log('[SupabaseClient] Tab became visible. checkRealtimeConnection() would run here.');
    // checkRealtimeConnection(); // TEMP: Disable direct call from global listener
  }
});

// Initialize public client instance
let publicSupabaseInstance = createClient<Database>(
  SUPABASE_URL,
  SUPABASE_ANON_KEY,
  {
    auth: {
      persistSession: false,
      autoRefreshToken: false,
      detectSessionInUrl: false,
    },
    global: {
      headers: {
        'x-client-info': 'roundone-web-public',
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    },
    db: {
      schema: 'public'
    }
  }
);

// Initialize service role client instance for bypassing RLS policies (use with caution)
let serviceRoleClient = createClient<Database>(
  SUPABASE_URL,
  SUPABASE_SERVICE_KEY,
  {
    auth: {
      persistSession: false,
      autoRefreshToken: false,
      detectSessionInUrl: false,
    },
    global: {
      headers: {
        'x-client-info': 'roundone-web-service-role',
      }
    },
    db: {
      schema: 'public'
    }
  }
);

// Export the clients
export const supabase = supabaseInstance;
export const publicSupabase = publicSupabaseInstance;
export const serviceSupabase = serviceRoleClient;

// Helper function to get redirect URL for OAuth
export const getRedirectUrl = () => {
  const url = new URL(window.location.href);
  const redirectTo = `${url.protocol}//${url.host}/auth/callback`;
  return redirectTo;
};

// Helper function for Google OAuth sign in
export const signInWithGoogle = async () => {
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo: getRedirectUrl(),
      queryParams: {
        access_type: 'offline',
        prompt: 'consent'
      }
    }
  });

  if (error) {
    console.error("Google sign in error:", error);
    throw error;
  }

  return data;
};

// Helper function to sign in with Microsoft
export const signInWithMicrosoft = async () => {
  try {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'azure',
      options: {
        redirectTo: getRedirectUrl(),
        scopes: 'email profile openid User.Read',
        queryParams: {
          prompt: 'select_account',
          response_type: 'code',
          tenant: 'common'
        }
      }
    });
    
    if (error) {
      console.error('Error signing in with Microsoft:', error);
      throw error;
    }
    
    return data;
  } catch (error) {
    console.error('Exception during Microsoft sign-in:', error);
    throw error;
  }
};

// Helper function for LinkedIn OAuth sign in
export const signInWithLinkedIn = async () => {
  try {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'linkedin_oidc',
      options: {
        redirectTo: getRedirectUrl(),
        scopes: 'openid profile email'
      }
    });
    
    if (error) {
      console.error('Error signing in with LinkedIn:', error);
      throw error;
    }
    
    return data;
  } catch (error) {
    console.error('Exception during LinkedIn sign-in:', error);
    throw error;
  }
};

// Test the connection and log the result
try {
  supabase.auth.getSession().then(({ data, error }) => {
    if (error) {
      console.error("Supabase connection error:", error);
    }
  });
} catch (error) {
  console.error("Exception during Supabase connection test:", error);
}
